import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Ban, Database, Loader2, RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { explain } from '@/shared/api/problem'
import { ErpTable, Td, Th } from '@/components/erp/primitives'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { fmtDateTime, fmtNumber, fmtRelative } from '@/shared/lib/format'
import { useCan } from '@/shared/auth/session'
import {
  cancelRun, getBrief, getRun, listRuns, reportForRun, runChildren,
  type Brief, type Report, type Run,
} from './api'
import { RUN_STATUS_LABEL, isLiveRun, usd } from './vocab'
import { CouncilBoard } from './components/CouncilBoard'
import { ReportView } from './components/ReportView'

/** Every council that has run, newest first. */
export function RunsPage() {
  const [runs, setRuns] = useState<Run[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      setRuns(await listRuns(0, 50))
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const live = (runs ?? []).some((r) => isLiveRun(r.status))
  useEffect(() => {
    if (!live) return
    const timer = setInterval(() => { void load() }, 4000)
    return () => clearInterval(timer)
  }, [live, load])

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-erp-lg font-semibold">Runs</h1>
          <p className="text-erp-xs text-muted-foreground mt-0.5">
            Everything this module has ever done is here — console playbooks, conversational
            turns, scheduled briefings and each unit of a map fan-out. There is no second,
            cheaper path that skips this list.
          </p>
        </div>
        <button type="button" onClick={() => void load()}
          className="erp-btn h-9 px-3 text-erp-sm inline-flex items-center gap-2">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      <ErrorStrip message={error} className="rounded-md border" />
      {runs === null && !error && <LoadingBlock />}

      {runs && runs.length === 0 && (
        <Note tone="info">Nothing has run yet.</Note>
      )}

      {runs && runs.length > 0 && (
        <div>
          <ErpTable head={
            <>
              <Th>Status</Th>
              <Th>Title</Th>
              <Th>Playbook</Th>
              <Th>Scope</Th>
              <Th>Trigger</Th>
              <Th className="text-right">Tokens</Th>
              <Th className="text-right">Cost</Th>
              <Th>Requested</Th>
            </>
          }>
            {runs.map((run) => (
              <tr key={run.id} className="border-t border-border hover:bg-muted/30">
                <Td>
                  <span className={cn('inline-flex items-center gap-1 text-erp-xs',
                    run.status === 'FAILED' && 'text-red-700 dark:text-red-400',
                    run.status === 'BLOCKED_BUDGET' && 'text-amber-700 dark:text-amber-400',
                    run.status === 'COMPLETED' && 'text-emerald-700 dark:text-emerald-400',
                    isLiveRun(run.status) && 'text-chocolate',
                  )}>
                    {isLiveRun(run.status) && <Loader2 className="h-3 w-3 animate-spin" />}
                    {RUN_STATUS_LABEL[run.status] ?? run.status}
                  </span>
                </Td>
                <Td>
                  <Link to={`/advisory/runs/${run.id}`} className="hover:text-chocolate">
                    {run.title}
                  </Link>
                  {run.childCount > 0 && (
                    <span className="ml-1.5 text-erp-xs text-muted-foreground">
                      +{run.childCount} units
                    </span>
                  )}
                </Td>
                <Td className="text-erp-xs text-muted-foreground">{run.playbookCode}</Td>
                <Td className="text-erp-xs text-muted-foreground">{run.scopeLabel}</Td>
                <Td className="text-erp-xs text-muted-foreground">{run.triggerKind}</Td>
                <Td className="text-right tabular-nums text-erp-xs">
                  {fmtNumber(run.inputTokens + run.outputTokens)}
                </Td>
                <Td className="text-right tabular-nums text-erp-xs">{usd(run.costUsd)}</Td>
                <Td className="text-erp-xs text-muted-foreground"
                  title={fmtDateTime(run.requestedAt)}>
                  {fmtRelative(run.requestedAt)}
                </Td>
              </tr>
            ))}
          </ErpTable>
        </div>
      )}
    </div>
  )
}

/**
 * One run: the council board, the report, and the brief the models were given.
 *
 * ## The brief is behind a click and worth the click
 *
 * “What did the machine actually see” is the whole argument when a report is
 * being questioned six months later, and it is a stored, hashed row rather than a
 * reconstruction. Opening it on a SENSITIVE run writes an audit entry.
 */
export function RunDetailPage() {
  const { id = '' } = useParams()
  const [run, setRun] = useState<Run | null>(null)
  const [report, setReport] = useState<Report | null>(null)
  const [children, setChildren] = useState<Run[]>([])
  const [brief, setBrief] = useState<Brief | null>(null)
  const [showBrief, setShowBrief] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const mayCancel = useCan('advisor:run')

  const load = useCallback(async () => {
    try {
      setError(null)
      const next = await getRun(id)
      setRun(next)
      if (next.childCount > 0) setChildren(await runChildren(id))
      if (next.reportId) {
        setReport(await reportForRun(id))
      }
    } catch (err) {
      setError(explain(err))
    }
  }, [id])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    if (!run || !isLiveRun(run.status)) return
    const timer = setInterval(() => { void load() }, 3000)
    return () => clearInterval(timer)
  }, [run, load])

  const openBrief = async () => {
    setShowBrief(true)
    if (!brief) {
      try {
        setBrief(await getBrief(id))
      } catch (err) {
        setError(explain(err))
      }
    }
  }

  return (
    <div className="space-y-4">
      <ErrorStrip message={error} className="rounded-md border" />
      {run === null && !error && <LoadingBlock />}

      {run && (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-erp-lg font-semibold">{run.title}</h1>
              <p className="text-erp-xs text-muted-foreground mt-0.5">
                {run.playbookCode} · {run.councilCode} · {run.scopeLabel} ·{' '}
                {fmtDateTime(run.requestedAt)}
              </p>
            </div>
            <div className="flex gap-2">
              {run.briefId && (
                <button type="button" onClick={() => void openBrief()}
                  className="erp-btn h-9 px-3 text-erp-sm inline-flex items-center gap-2">
                  <Database className="h-3.5 w-3.5" /> What the models were given
                </button>
              )}
              {mayCancel && run.status === 'QUEUED' && (
                <button type="button"
                  onClick={() => void cancelRun(id).then(() => load())}
                  className="erp-btn h-9 px-3 text-erp-sm inline-flex items-center gap-2">
                  <Ban className="h-3.5 w-3.5" /> Cancel
                </button>
              )}
            </div>
          </div>

          {run.status === 'FAILED' && run.errorMessage && (
            <Note tone="bad">
              <span className="font-medium">{run.errorCode ?? 'Failed'}. </span>
              {run.errorMessage}
            </Note>
          )}
          {run.status === 'BLOCKED_BUDGET' && (
            <Note tone="info">
              <span className="font-medium">Stopped by the budget. </span>
              {run.errorMessage ?? 'The monthly ceiling would have been crossed.'}
            </Note>
          )}

          <section>
            <h2 className="text-erp-sm font-medium mb-1.5">The council</h2>
            <CouncilBoard run={run} />
          </section>

          {children.length > 0 && (
            <section>
              <h2 className="text-erp-sm font-medium mb-1.5">
                Per-unit runs ({children.length})
              </h2>
              <ul className="grid gap-1"
                style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                {children.map((child) => (
                  <li key={child.id}>
                    <Link to={`/advisory/runs/${child.id}`}
                      className="flex items-center justify-between gap-2 rounded-md border
                        border-border px-2.5 py-1.5 text-erp-xs hover:border-chocolate/50">
                      <span className="truncate">{child.scopeLabel}</span>
                      <span className={cn('shrink-0',
                        child.status === 'COMPLETED' && 'text-emerald-600',
                        child.status === 'FAILED' && 'text-red-600',
                        isLiveRun(child.status) && 'text-chocolate')}>
                        {RUN_STATUS_LABEL[child.status] ?? child.status}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {report && (
            <section className="rounded-md border border-border p-3">
              <ReportView report={report} onProposalChanged={() => void load()} />
            </section>
          )}

          {showBrief && brief && (
            <BriefView brief={brief} onClose={() => setShowBrief(false)} />
          )}
        </>
      )}
    </div>
  )
}

/** The stored brief — the sections, what was withheld, and the exact markdown sent. */
function BriefView({ brief, onClose }: { brief: Brief; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      role="dialog" aria-modal="true">
      <div className="flex max-h-[85vh] w-full max-w-4xl flex-col rounded-md border border-border
        bg-background">
        <div className="flex items-start justify-between gap-3 border-b border-border p-3">
          <div>
            <h2 className="text-erp-sm font-semibold">
              The evidence brief — {brief.scopeLabel}
            </h2>
            <p className="text-erp-xs text-muted-foreground mt-0.5">
              Built {fmtDateTime(brief.builtAt)} · {fmtNumber(brief.tokenEstimate)} tokens ·
              hash {brief.contentHash.slice(0, 12)}…
            </p>
            <p className="text-erp-xs text-muted-foreground/80 mt-0.5">
              This is exactly what was sent. It is stored rather than reconstructed, so it
              cannot be re-rendered differently later.
            </p>
          </div>
          <button type="button" onClick={onClose} className="erp-btn h-8 px-2.5 text-erp-xs">
            Close
          </button>
        </div>

        {brief.redactions.length > 0 && (
          <div className="border-b border-border bg-muted/30 px-3 py-2">
            <div className="text-erp-xs font-medium">
              Withheld before sending ({brief.redactions.length})
            </div>
            <ul className="mt-0.5 space-y-0.5">
              {brief.redactions.slice(0, 12).map((r, i) => (
                <li key={i} className="text-erp-xs text-muted-foreground">
                  <code className="rounded bg-muted px-1">
                    {String(r.dataset)}.{String(r.target)}
                  </code>{' '}
                  — {String(r.reason)} ({String(r.count)})
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-auto p-3">
          <pre className="whitespace-pre-wrap text-erp-xs leading-relaxed">{brief.markdown}</pre>
        </div>

        <div className="border-t border-border px-3 py-2 text-erp-xs text-muted-foreground">
          Built for a caller holding: {brief.authoritySnapshot.join(', ') || '—'}
        </div>
      </div>
    </div>
  )
}
